import React, { useState, useEffect, useRef } from 'react';
import { Plus, Wrench, Calculator, X, Mail, Info, Clock, Check, RefreshCw, HelpCircle, MapPin, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import Modal from '../Modal';
import QuoteCalculator from '../QuoteCalculator';
import { formatCurrency } from '../../utils/helpers';
import { customersApi } from '../../services/api';
import api from '../../services/api';
import businessService from '../../services/businessService';
import AssetSelectorSection from '../customers/AssetSelectorSection';
import { useBusinessContext } from '../../contexts/BusinessContext';

// ============================================
// HELPER: Get theme colors from localStorage
// ============================================
const getThemeColors = () => {
  try {
    const stored = localStorage.getItem('appTheme');
    if (stored) {
      const theme = JSON.parse(stored);
      const hex = theme.primaryColor || '#3b82f6';
      // Convert hex to RGB
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (result) {
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        
        // Calculate relative luminance to detect light colors
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const isLightColor = luminance > 0.6;
        
        // For light colors (like yellow), create a darker text variant
        let textR = r, textG = g, textB = b;
        if (isLightColor) {
          textR = Math.round(r * 0.6);
          textG = Math.round(g * 0.6);
          textB = Math.round(b * 0.6);
        }
        
        return {
          primary: hex,
          text: isLightColor ? `rgb(${textR}, ${textG}, ${textB})` : hex,
          light: `rgba(${r}, ${g}, ${b}, 0.15)`,
          lighter: `rgba(${r}, ${g}, ${b}, 0.08)`,
          border: `rgba(${r}, ${g}, ${b}, 0.35)`,
        };
      }
    }
  } catch (e) {
    console.error('Error reading theme:', e);
  }
  return {
    primary: '#3b82f6',
    text: '#3b82f6',
    light: 'rgba(59, 130, 246, 0.15)',
    lighter: 'rgba(59, 130, 246, 0.08)',
    border: 'rgba(59, 130, 246, 0.35)',
  };
};

// ============================================
// TOOLTIP COMPONENT
// ============================================
const Tooltip = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };
  
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="ml-1.5 p-1 text-gray-400 hover:text-blue-600 focus:outline-none rounded-full hover:bg-blue-50 active:bg-blue-100"
        aria-label="More information"
      >
        <HelpCircle size={14} />
      </button>
      
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
          onClick={handleClose}
        >
          <div 
            className="bg-gray-900 text-white text-sm rounded-xl shadow-2xl max-w-sm w-full p-4 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <p className="font-semibold text-base mb-2 text-blue-300">{title}</p>
            )}
            <div className="text-gray-200 space-y-2">{children}</div>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Modal for creating and editing jobs with multi-service support
 * Financial data (pricing) is hidden from team members - only owners see prices
 * 
 * v2: Added pricing history integration - shows "Previously charged" for returning customers
 */
const JobFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  editingJob,
  customers,
  serviceTypes,
  emailSettings,
  isOwner,
  onReopen,
  onNotify,
}) => {
  const { isSimpleMode } = useBusinessContext();

  // Theme colors from localStorage
  const [themeColors, setThemeColors] = useState(getThemeColors);
  
  // Refresh theme colors when modal opens
  useEffect(() => {
    if (isOpen) {
      setThemeColors(getThemeColors());
    }
  }, [isOpen]);

  // Load business profile + zone settings once when modal first opens
  useEffect(() => {
    if (isOpen && !businessProfile) {
      Promise.all([
        businessService.getProfile(),
        businessService.getRouteSettings(),
      ]).then(([profileRes, zoneRes]) => {
        setBusinessProfile(profileRes.data);
        setZoneSettings(zoneRes.data);
      }).catch(err => console.log('Could not load zone settings:', err));
    }
    if (!isOpen) {
      setZoneInfo(null);
      setZoneOverridden(false);
    }
  }, [isOpen]);

  // Base job form data
  const [formData, setFormData] = useState({
    customerId: '',
    address: '',
    city: '',
    state: 'NC',
    zipCode: '',
    scheduledDate: '',
    scheduledTime: '',
    description: '',
    status: 'Quoted',
    estimatedDurationMinutes: null,
  });

  // Track if using different service address
  const [useDifferentAddress, setUseDifferentAddress] = useState(false);
  
  // Store the customer's address for reference
  const [customerAddress, setCustomerAddress] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
  });

  // Multi-service state
  const [jobServices, setJobServices] = useState([]);
  
  // Track which services have notes expanded
  const [expandedNotes, setExpandedNotes] = useState({});

  // Email override state
  const [emailOverrides, setEmailOverrides] = useState({
    sendConfirmationEmail: false,
    sendCompletedEmail: false,
  });

  // Asset selection state
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [assetPatch, setAssetPatch] = useState(null);
  const [assetExtraData, setAssetExtraData] = useState({});
  const [industryUsesAssets, setIndustryUsesAssets] = useState(false); // mileageIn, behaviorNotes, etc.

  // Inline customer creation
  const [showInlineCustomerForm, setShowInlineCustomerForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'NC',
    zipCode: '',
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // Pricing history for the selected customer
  const [pricingHistory, setPricingHistory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricingHistoryLoading, setPricingHistoryLoading] = useState(false);

  // Distance zone pricing state
  const [businessProfile, setBusinessProfile] = useState(null);
  const [zoneSettings, setZoneSettings] = useState(null);
  const [zoneInfo, setZoneInfo] = useState(null); // { zoneName, zoneNum, distanceMiles, pct }
  const [zoneOverridden, setZoneOverridden] = useState(false);
  const geocodeTimerRef = useRef(null);

  // Discount state
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountReason, setDiscountReason] = useState('Veteran');
  const [discountCustomReason, setDiscountCustomReason] = useState('');
  const [roundedTotal, setRoundedTotal] = useState(null);

  // Base service total — always the sum of services
  const serviceTotal = jobServices.reduce((sum, s) => sum + (s.totalPrice || 0), 0);
  const totalQuoteAmount = serviceTotal;

  
  // Selected customer info
  const selectedCustomer = formData.customerId 
    ? customers.find(c => c.id.toString() === formData.customerId.toString())
    : null;
  const customerHasEmail = selectedCustomer?.email ? true : false;
  const isJobLocked = !!editingJob && (editingJob.status === 'Completed' || editingJob.isInvoiced === true);

  // Check if scheduled date is required
  const scheduledDateRequired = formData.status === 'Scheduled' || formData.status === 'In Progress';

  // Email tracking from editingJob
  const confirmationAlreadySent = editingJob?.confirmationEmailSent || false;
  const confirmationSentDate = editingJob?.confirmationEmailSentDate;

  // Check if status has email enabled
  const statusEmailMap = {
    'Scheduled': ['sendJobConfirmation', 'sendServicePrep', 'sendJobReminder'],
    'Completed': ['sendJobCompleted', 'sendReviewRequest'],
  };

  const statusHasEmailEnabled = (status) => {
    const emailTypes = statusEmailMap[status];
    if (!emailTypes) return false;
    return emailTypes.some(type => emailSettings[type]);
  };

  // Helper to parse time from ISO date string
  const parseTimeFromDate = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) {
        return '';
      }
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Format date for display
  const formatSentDate = (dateString) => {
    if (!dateString) return '';
    try {
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  // ============================================
  // PRICING HISTORY HELPERS
  // ============================================
  
  // Debounced zone calculation when address changes
  useEffect(() => {
    if (!isOwner || !zoneSettings || !businessProfile?.latitude) return;
    const fullAddress = [formData.address, formData.city, formData.state, formData.zipCode]
      .filter(Boolean).join(', ');
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = setTimeout(() => {
      geocodeAndCalculateZone(fullAddress, businessProfile, zoneSettings);
    }, 800);
    return () => clearTimeout(geocodeTimerRef.current);
  }, [formData.address, formData.city, formData.state, formData.zipCode, zoneSettings, businessProfile]);

  // Fetch pricing history when customer changes
  useEffect(() => {
    const fetchPricingHistory = async () => {
      if (!formData.customerId || !isOwner) {
        setPricingHistory([]);
        return;
      }
      
      setPricingHistoryLoading(true);
      try {
        const response = await customersApi.getPricingHistory(formData.customerId);
        setPricingHistory(response.data || []);
      } catch (err) {
        console.log('Could not load pricing history:', err);
        setPricingHistory([]);
      } finally {
        setPricingHistoryLoading(false);
      }
    };
    
    fetchPricingHistory();
  }, [formData.customerId, isOwner]);

  // Get pricing history for a specific service type
  const getPricingHistoryForService = (serviceTypeId) => {
    if (!serviceTypeId || !pricingHistory.length) return null;
    return pricingHistory.find(ph => ph.serviceTypeId?.toString() === serviceTypeId?.toString());
  };
  
  // Handle "Use Previous" click - fills measurement into calculator
  const handleUsePreviousPricing = (index, historyItem) => {
    if (!historyItem) return;
    
    let measurementToUse = historyItem.measurementValue;
    
    // If no measurement stored but we have price and rate, calculate it
    if (!measurementToUse && historyItem.lastPrice) {
      const service = jobServices[index];
      const serviceType = getServiceType(service?.serviceTypeId);
      if (serviceType?.baseRate && serviceType.baseRate > 0) {
        measurementToUse = Math.round(historyItem.lastPrice / serviceType.baseRate);
      }
    }
    
    if (!measurementToUse) return;
    
    // Update the service's initialValues to trigger QuoteCalculator re-render
    // Also update tempId to force the component to remount with new initial values
    updateService(index, {
      tempId: Date.now(), // Force remount
      initialValues: {
        measurementValue: measurementToUse,
        selectedTier: '',
        activeModifiers: {},
      },
    });
  };

  // ============================================
  // HELPER: Parse saved breakdown to initialValues
  // ============================================
  const parseBreakdownToInitialValues = (quoteBreakdownJson) => {
    if (!quoteBreakdownJson) return {};
    
    try {
      const breakdown = JSON.parse(quoteBreakdownJson);
      
      const measurementValue = breakdown.measurementValue ?? breakdown.MeasurementValue ?? '';
      const selectedTier = breakdown.selectedTier ?? breakdown.SelectedTier ?? '';
      const priceOverride = breakdown.priceOverride ?? breakdown.PriceOverride ?? null;
      
      let activeModifiers = {};
      const rawModifiers = breakdown.activeModifiers ?? breakdown.ActiveModifiers;
      
      if (rawModifiers) {
        if (Array.isArray(rawModifiers)) {
          rawModifiers.forEach(mod => {
            const modName = mod.name || mod.Name;
            if (modName) {
              activeModifiers[modName] = true;
            }
          });
        } else if (typeof rawModifiers === 'object') {
          activeModifiers = rawModifiers;
        }
      }
      
      return {
        measurementValue,
        selectedTier,
        activeModifiers,
        priceOverride,
      };
    } catch (e) {
      console.error('Failed to parse quote breakdown:', e);
      return {};
    }
  };

  // ============================================
  // CUSTOMER SELECTION HANDLER
  // ============================================
  const handleCustomerChange = (customerId) => {
    // A different customer means a different set of properties: clear the previous choice
    setSelectedAssetId(null);
    setUseDifferentAddress(false);
    const customer = customers.find(c => c.id.toString() === customerId);
    
    setFormData(prev => ({ 
      ...prev, 
      customerId,
      status: (customer?.billingType && customer.billingType !== 'PerJob') 
        ? 'Scheduled' 
        : prev.status
    }));
    
    if (customer) {
      const custAddr = {
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || 'NC',
        zipCode: customer.zipCode || '',
      };
      setCustomerAddress(custAddr);
      
      if (!useDifferentAddress || !editingJob) {
        setFormData(prev => ({
          ...prev,
          customerId,
          address: custAddr.address,
          city: custAddr.city,
          state: custAddr.state,
          zipCode: custAddr.zipCode,
        }));
      }
    }
  };

  // Handle toggle for different address
  const handleDifferentAddressToggle = (checked) => {
    setUseDifferentAddress(checked);
    
    if (!checked && selectedCustomer) {
      setFormData(prev => ({
        ...prev,
        address: customerAddress.address,
        city: customerAddress.city,
        state: customerAddress.state,
        zipCode: customerAddress.zipCode,
      }));
    } else if (checked) {
      setFormData(prev => ({
        ...prev,
        address: '',
        city: '',
        state: 'NC',
        zipCode: '',
      }));
    }
  };

  // Initialize form when modal opens or editingJob changes
  useEffect(() => {
    if (isOpen) {
      if (editingJob) {
        const customer = customers.find(c => c.id === editingJob.customerId);
        const jobAddrDifferent = customer && (
          editingJob.address !== customer.address ||
          editingJob.city !== customer.city ||
          editingJob.zipCode !== customer.zipCode
        );
        
        setUseDifferentAddress(jobAddrDifferent);
        
        if (customer) {
          setCustomerAddress({
            address: customer.address || '',
            city: customer.city || '',
            state: customer.state || 'NC',
            zipCode: customer.zipCode || '',
          });
        }
        
        setFormData({
          customerId: editingJob.customerId?.toString() || '',
          address: editingJob.address || '',
          city: editingJob.city || '',
          state: editingJob.state || 'NC',
          zipCode: editingJob.zipCode || '',
          scheduledDate: editingJob.scheduledDate?.split('T')[0] || '',
          scheduledTime: parseTimeFromDate(editingJob.scheduledDate),
          description: editingJob.description || '',
          status: editingJob.status,
          estimatedDurationMinutes: editingJob.estimatedDurationMinutes,
        });

        if (editingJob.jobServices && editingJob.jobServices.length > 0) {
          setJobServices(editingJob.jobServices.map((js, idx) => ({
            tempId: Date.now() + idx,
            id: js.id,
            serviceTypeId: js.serviceTypeId?.toString() || '',
            quantity: js.quantity || 1,
            unitPrice: js.unitPrice,
            totalPrice: js.totalPrice || 0,
            notes: js.notes || '',
            quoteBreakdownJson: js.quoteBreakdownJson,
            initialValues: parseBreakdownToInitialValues(js.quoteBreakdownJson),
          })));
          
          const notesState = {};
          editingJob.jobServices.forEach((js, idx) => {
            if (js.notes) notesState[idx] = true;
          });
          setExpandedNotes(notesState);
        } else {
          setJobServices([createEmptyService()]);
          setExpandedNotes({});
        }

        setEmailOverrides({
          sendConfirmationEmail: false,
          sendCompletedEmail: false,
        });


        // Restore zone info and discount from saved breakdown if present
        try {
          if (editingJob.quoteBreakdownJson) {
            const breakdown = JSON.parse(editingJob.quoteBreakdownJson);
            if (breakdown.zoneInfo) {
              setZoneInfo(breakdown.zoneInfo);
              setZoneOverridden(breakdown.zoneOverridden === true);
            }
            if (breakdown.discountReason) {
              setDiscountEnabled(true);
              if (['Veteran', 'Senior', 'First Responder'].includes(breakdown.discountReason)) {
                setDiscountReason(breakdown.discountReason);
              } else {
                setDiscountReason('Custom');
                setDiscountCustomReason(breakdown.discountReason);
              }
            }
            if (breakdown.roundedTotal) {
              setRoundedTotal(breakdown.roundedTotal);
            }
          }
        } catch (e) {
          // silently ignore parse errors
        }

        // Initialize asset selection if job has one
        setSelectedAssetId(editingJob.customerAssetId || null);
        setAssetExtraData({
          mileageIn: editingJob.mileageIn || null,
          behaviorNotes: editingJob.behaviorNotes || null,
          serviceFormula: editingJob.serviceFormula || null,
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, editingJob]);

  const createEmptyService = () => ({
    tempId: Date.now(),
    serviceTypeId: '',
    quantity: 1,
    unitPrice: null,
    totalPrice: 0,
    notes: '',
    quoteBreakdownJson: null,
    initialValues: {},
  });

  const resetForm = () => {
    setFormData({
      customerId: '',
      address: '',
      city: '',
      state: 'NC',
      zipCode: '',
      scheduledDate: isSimpleMode ? new Date().toLocaleDateString('en-CA') : '',
      scheduledTime: '',
      description: '',
      status: isOwner && !isSimpleMode ? 'Quoted' : 'Scheduled',
      estimatedDurationMinutes: null,
    });
    setUseDifferentAddress(false);
    setCustomerAddress({ address: '', city: '', state: '', zipCode: '' });
    setJobServices([createEmptyService()]);
    setExpandedNotes({});
    setEmailOverrides({
      sendConfirmationEmail: emailSettings.sendJobConfirmation,
      sendCompletedEmail: emailSettings.sendJobCompleted,
    });
    setShowInlineCustomerForm(false);
    setNewCustomerData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'NC',
      zipCode: '',
    });
    setPricingHistory([]);
    setSelectedAssetId(null);
    setSelectedProperty(null);
    setAssetPatch(null);
    setAssetExtraData({});
    setDiscountEnabled(false);
    setDiscountReason('Veteran');
    setDiscountCustomReason('');
    setRoundedTotal(null);
  };

  // Service helpers
  const addService = () => {
    const newIndex = jobServices.length;
    setJobServices(prev => [...prev, createEmptyService()]);
    setExpandedNotes(prev => ({ ...prev, [`editing_${newIndex}`]: true }));
  };

  const removeService = (index) => {
    setJobServices(prev => prev.filter((_, i) => i !== index));
    setExpandedNotes(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const updateService = (index, updates) => {
    setJobServices(prev => prev.map((service, i) => 
      i === index ? { ...service, ...updates } : service
    ));
  };

  const toggleServiceNotes = (index) => {
    setExpandedNotes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getServiceType = (serviceTypeId) => {
    return serviceTypes.find(st => st.id.toString() === serviceTypeId?.toString());
  };

  // Save a measurement (sq ft, stories...) back to the selected property
  const handleSaveToProperty = async (updates, source = 'manual') => {
    if (!selectedProperty || !formData.customerId) return;
    try {
      const res = await api.patch(
        `/customers/${formData.customerId}/assets/${selectedProperty.id}/attributes`,
        { values: updates, source }
      );
      setAssetPatch({ id: selectedProperty.id, attributesJson: res.data.attributesJson });
      onNotify?.('Saved to property', 'success');
    } catch (err) {
      console.error('Error saving to property:', err);
      onNotify?.(err.response?.data?.message || 'Could not save to the property.');
    }
  };

  const handleServiceTypeChange = (index, serviceTypeId) => {
    const serviceType = serviceTypes.find(st => st.id.toString() === serviceTypeId);
    
    updateService(index, {
      serviceTypeId,
      unitPrice: serviceType && !serviceType.pricingTemplate && serviceType.defaultPrice
        ? serviceType.defaultPrice
        : null,
      totalPrice: serviceType && !serviceType.pricingTemplate && serviceType.defaultPrice
        ? serviceType.defaultPrice
        : 0,
      quoteBreakdownJson: null,
      initialValues: {},
    });
  };

  const handleQuoteCalculated = (index, result) => {
    if (result && result.total > 0) {
      const service = jobServices[index];
      const serviceType = serviceTypes.find(st => st.id.toString() === service.serviceTypeId);
      
      const breakdownWithContext = {
        ...result,
        serviceTypeName: serviceType?.name,
        pricingTemplate: serviceType?.pricingTemplate,
      };
      
      updateService(index, {
        totalPrice: result.total,
        quoteBreakdownJson: JSON.stringify(breakdownWithContext),
        initialValues: {
          measurementValue: result.measurementValue,
          selectedTier: result.selectedTier,
          activeModifiers: result.activeModifiers,
          priceOverride: result.priceOverride,
        },
      });
    }
  };

  const handleFormStatusChange = (newStatus) => {
    setFormData({ ...formData, status: newStatus });
    
    if (newStatus === 'Scheduled' && !editingJob) {
      setEmailOverrides(prev => ({
        ...prev,
        sendConfirmationEmail: emailSettings.sendJobConfirmation,
      }));
    } else if (newStatus === 'Completed' && !editingJob) {
      setEmailOverrides(prev => ({
        ...prev,
        sendCompletedEmail: emailSettings.sendJobCompleted,
      }));
    }
  };

  const buildCombinedBreakdown = () => {
    const validServices = jobServices.filter(s => s.serviceTypeId && s.totalPrice > 0);
    
    if (validServices.length === 0) return null;
    
    const breakdown = {
      total: finalTotal,
      baseTotal: totalQuoteAmount,
      zoneInfo: zoneInfo || null,
      zoneOverridden: zoneOverridden,
      discountReason: discountEnabled
        ? (discountReason === 'Custom' ? discountCustomReason : discountReason)
        : null,
      rounded: roundedTotal !== null,
      roundedTotal: roundedTotal !== null ? roundedTotal : null,
      services: validServices.map(s => {
        const st = getServiceType(s.serviceTypeId);
        return {
          serviceTypeName: st?.name,
          quantity: s.quantity,
          totalPrice: s.totalPrice,
          breakdown: s.quoteBreakdownJson ? JSON.parse(s.quoteBreakdownJson) : null,
        };
      }),
    };
    
    return JSON.stringify(breakdown);
  };

  const combineDateTime = (date, time) => {
    if (!date) return null;
    if (time) {
      return `${date}T${time}:00.000Z`;
    } else {
      return `${date}T00:00:00.000Z`;
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (scheduledDateRequired && !formData.scheduledDate) {
    onNotify?.(`Please select a scheduled date when status is "${formData.status}".`);
    return;
  }
  
  if (!formData.address) {
    onNotify?.('Please enter a service address.');
    return;
  }
  
  setIsSubmitting(true);
  
  try {
    const validServices = jobServices.filter(s => s.serviceTypeId && (isOwner ? s.totalPrice > 0 : true));
    const combinedScheduledDate = combineDateTime(formData.scheduledDate, formData.scheduledTime);
    
    const submitData = {
      formData: {
        ...formData,
        scheduledDate: combinedScheduledDate,
        customerAssetId: selectedAssetId,
        mileageIn: assetExtraData.mileageIn || null,
        behaviorNotes: assetExtraData.behaviorNotes || null,
        serviceFormula: assetExtraData.serviceFormula || null,
      },
      jobServices: validServices,
      totalQuoteAmount: isOwner ? finalTotal : (editingJob?.quoteAmount || 0),
      emailOverrides,
      quoteBreakdownJson: isOwner ? buildCombinedBreakdown() : editingJob?.quoteBreakdownJson,
    };
    
    await onSubmit(submitData);
  } catch (error) {
    console.error('Error submitting job form:', error);
    onNotify?.(error.response?.data?.message || 'Could not save the job. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCreateInlineCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomerData.name || !newCustomerData.address) {
      onNotify?.('Name and address are required to create a customer.');
      return;
    }
    
    setCreatingCustomer(true);
    
    try {
      const customerPayload = {
        name: newCustomerData.name.trim(),
        address: newCustomerData.address.trim(),
        email: newCustomerData.email.trim() || null,
        phone: newCustomerData.phone.trim() || null,
        city: newCustomerData.city.trim() || null,
        state: newCustomerData.state || null,
        zipCode: newCustomerData.zipCode.trim() || null,
        notes: null,
        isActive: true,
      };
      
      if (window._jobFormCreateCustomer) {
        const newCustomer = await window._jobFormCreateCustomer(customerPayload);
        if (newCustomer) {
          const custAddr = {
            address: newCustomer.address || '',
            city: newCustomer.city || '',
            state: newCustomer.state || 'NC',
            zipCode: newCustomer.zipCode || '',
          };
          setCustomerAddress(custAddr);
          
          setFormData(prev => ({ 
            ...prev, 
            customerId: newCustomer.id.toString(),
            address: custAddr.address,
            city: custAddr.city,
            state: custAddr.state,
            zipCode: custAddr.zipCode,
          }));
          
          setUseDifferentAddress(false);
          setShowInlineCustomerForm(false);
          setNewCustomerData({
            name: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: 'NC',
            zipCode: '',
          });
        }
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      onNotify?.('Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Generic time slots for public forms / team member view
  const commonTimeSlots = [
    { label: 'Morning (8 AM)', value: '08:00' },
    { label: 'Mid-Morning (10 AM)', value: '10:00' },
    { label: 'Noon (12 PM)', value: '12:00' },
  ];

  // ============================================
  // PRICING HISTORY HINT COMPONENT
  // ============================================
  const PricingHistoryHint = ({ serviceTypeId, index, compact = false }) => {
    const history = getPricingHistoryForService(serviceTypeId);
    const serviceType = getServiceType(serviceTypeId);

    // Only show for calculator-based services with history
    if (!history || !serviceType?.pricingTemplate) return null;

    const hasSize = !!(history.measurementValue && history.measurementUnit);
    const sizeText = hasSize
      ? `${history.measurementValue.toLocaleString()} ${history.measurementUnit}`
      : null;
    const dateText = history.lastJobDate
      ? new Date(history.lastJobDate).toLocaleDateString()
      : null;

    // Without a stored size there is nothing for the button to fill in
    const button = hasSize ? (
      <button
        type="button"
        onClick={() => handleUsePreviousPricing(index, history)}
        title="Fills in the measurement from last time. Add-ons and any override price are not carried over."
        className={`${compact ? 'px-2 py-1 rounded' : 'px-2.5 py-1 rounded-md'} text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors whitespace-nowrap flex-shrink-0`}
      >
        Use size
      </button>
    ) : null;

    if (compact) {
      return (
        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-green-800">
              <TrendingUp size={12} className="inline mr-1" />
              <span className="font-medium">Last:</span> {formatCurrency(history.lastPrice)}
              {sizeText && <span className="text-green-600 ml-1">· {sizeText}</span>}
            </p>
            {button}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-1.5 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-md flex items-center justify-between gap-2">
        <p className="text-xs text-green-800 flex items-center gap-1.5 min-w-0">
          <TrendingUp size={12} className="text-green-600 flex-shrink-0" />
          <span className="truncate">
            <span className="font-medium">Last charged:</span> {formatCurrency(history.lastPrice)}
            {sizeText && <span className="text-green-600"> · {sizeText}</span>}
            {dateText && <span className="text-green-600"> · {dateText}</span>}
          </span>
        </p>
        {button}
      </div>
    );
  };

  // ============================================
  // DISTANCE ZONE HELPERS
  // ============================================
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const determineZone = (distanceMiles, settings) => {
    if (!settings) return null;
    const z1Miles = settings.distanceZone1Miles || 10;
    const z2Miles = settings.distanceZone2Miles || 20;
    const z2On = settings.distanceZone2Enabled;
    const z3On = settings.distanceZone3Enabled;

    let zoneNum = 1;
    let zoneName = 'Local';
    let pct = 0; // Zone 1 is always baseline, no adjustment

    if (z3On && distanceMiles > z2Miles) {
      zoneNum = 3; zoneName = 'Extended';
      pct = (settings.distanceZone3Multiplier - 1) * 100;
    } else if (z2On && distanceMiles > z1Miles) {
      zoneNum = 2; zoneName = 'Regional';
      pct = (settings.distanceZone2Multiplier - 1) * 100;
    }

    return { zoneNum, zoneName, distanceMiles: Math.round(distanceMiles * 10) / 10, pct: Math.round(pct * 10) / 10 };
  };

  const geocodeAndCalculateZone = async (address, profile, settings) => {
    if (!profile?.latitude || !profile?.longitude || !settings) return;
    if (!address || address.replace(/[,\s]/g, '').length < 5) return;

    try {
      const response = await api.get(`/business/geocode?address=${encodeURIComponent(address)}`);
      const data = response.data;
      if (!data.lat || !data.lng) return;

      const { lat, lng } = data;
      const distance = haversineDistance(
        parseFloat(profile.latitude),
        parseFloat(profile.longitude),
        lat, lng
      );
      const zone = determineZone(distance, settings);
      setZoneInfo(zone);
    } catch (err) {
      console.log('Zone geocode failed silently:', err);
    }
  };

  // Computed adjusted total
  const zoneMultiplier = (!zoneOverridden && zoneInfo && zoneInfo.pct !== 0)
    ? 1 + zoneInfo.pct / 100
    : 1;
  const adjustedTotal = totalQuoteAmount > 0
    ? Math.round(totalQuoteAmount * zoneMultiplier * 100) / 100
    : 0;

  // Display total: always use adjustedTotal which respects zoneOverridden
  const displayTotal = adjustedTotal || totalQuoteAmount;

  // Round-to suggestions based on displayTotal
  const getRoundSuggestions = (amount) => {
    if (!amount) return [];
    const base = Math.floor(amount / 5) * 5;
    const suggestions = new Set();
    for (let i = -2; i <= 3; i++) {
      const val = base + i * 5;
      if (val > 0 && Math.abs(val - amount) <= 20) suggestions.add(val);
    }
    return [...suggestions].sort((a, b) => a - b).slice(0, 4);
  };

  // Final total — rounded takes priority, then display
  const finalTotal = roundedTotal !== null ? roundedTotal : displayTotal;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingJob 
        ? (isOwner ? 'Edit Job' : 'Reschedule Job') 
        : 'Create New Job'}
    >
      <form onSubmit={handleSubmit} className="space-y-4" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault(); }}>
        
        {/* ============================================ */}
        {/* TEAM MEMBER VIEW - Simplified for rescheduling */}
        {/* ============================================ */}
        {!isOwner && editingJob ? (
          <>
            {/* Job Summary - Read Only */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                {selectedCustomer?.name || 'Customer'}
              </h3>
              <p className="text-sm text-gray-600">{formData.address}</p>
              {editingJob.jobServices?.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {editingJob.jobServices.map(js => js.serviceType?.name).filter(Boolean).join(', ')}
                </p>
              )}
            </div>

            {/* Scheduled Date & Time - Editable */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  Scheduled Date
                  {scheduledDateRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                  className={`input ${
                    scheduledDateRequired && !formData.scheduledDate 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : ''
                  }`}
                />
              </div>

              <div>
                <label className="label flex items-center gap-1.5">
                  <Clock size={14} className="text-gray-400" />
                  Scheduled Time
                </label>
                <input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            {/* Quick time buttons */}
            {!formData.scheduledTime && (
              <div className="flex flex-wrap gap-1">
                {commonTimeSlots.map((slot) => (
                  <button
                    key={slot.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, scheduledTime: slot.value })}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                  >
                    {slot.label.split(' ')[0]} {slot.label.match(/\(([^)]+)\)/)?.[1]}
                  </button>
                ))}
              </div>
            )}

            {/* Status - Limited options */}
            <div>
              <label className="label">Status</label>
              {editingJob?.status === 'Completed' ? (
                <div className="input bg-gray-100 text-gray-700 cursor-not-allowed flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Completed
                </div>
              ) : (
                <select
                  value={formData.status}
                  onChange={(e) => handleFormStatusChange(e.target.value)}
                  className="input"
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              )}
            </div>

            {/* Confirmation Email - if status is Scheduled */}
            {formData.status === 'Scheduled' && customerHasEmail && (
              <div className={`border rounded-lg p-3 ${
                confirmationAlreadySent 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                {confirmationAlreadySent ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check size={16} className="text-green-600" />
                      <span className="text-sm font-medium">Confirmation email sent</span>
                      {confirmationSentDate && (
                        <span className="text-xs text-green-600">
                          on {formatSentDate(confirmationSentDate)}
                        </span>
                      )}
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailOverrides.sendConfirmationEmail}
                        onChange={(e) => setEmailOverrides({ ...emailOverrides, sendConfirmationEmail: e.target.checked })}
                        className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm text-gray-700 flex items-center gap-1.5">
                          <RefreshCw size={14} className="text-blue-600" />
                          Resend confirmation (date/time changed)
                        </span>
                      </div>
                    </label>
                  </div>
                ) : (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailOverrides.sendConfirmationEmail}
                      onChange={(e) => setEmailOverrides({ ...emailOverrides, sendConfirmationEmail: e.target.checked })}
                      className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        <Mail size={14} className="text-blue-600" />
                        Send confirmation email
                      </span>
                    </div>
                  </label>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="label">Notes</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
                rows="2"
                placeholder="Add notes about this job..."
              />
            </div>
          </>
        ) : (
          <>
            {/* ============================================ */}
            {/* OWNER VIEW - Full editing capabilities */}
            {/* ============================================ */}
            
            {isJobLocked && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start justify-between gap-3">
                <div className="text-sm text-amber-900">
                  <p className="font-medium">
                    This job is {editingJob.status === 'Completed' ? 'completed' : 'invoiced'} and locked.
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Services, pricing, customer, and schedule can't be changed. Notes are still editable.
                  </p>
                </div>
                {isOwner && onReopen && !editingJob.isInvoiced && (
                  <button
                    type="button"
                    onClick={() => onReopen(editingJob)}
                    className="px-3 py-1.5 text-xs font-medium border border-amber-300 text-amber-800 hover:bg-amber-100 rounded-lg whitespace-nowrap"
                  >
                    Reopen job
                  </button>
                )}
                {isOwner && editingJob.isInvoiced && (
                  <Tooltip title="Need to change this job?">
                    <p>This job has an invoice, so its price and services are locked to keep the job, invoice, and payment records in agreement.</p>
                    <p className="mt-2"><strong>Invoice not paid yet:</strong> delete or void the invoice on the Invoices page, then come back and click Reopen job.</p>
                    <p className="mt-2"><strong>Already paid:</strong> don't reopen it. Record a refund or credit instead, and create a new job for any extra work.</p>
                    <p className="mt-2">Notes can always be edited.</p>
                  </Tooltip>
                )}
              </div>
            )}

            <fieldset disabled={isJobLocked} className="space-y-4 min-w-0 disabled:opacity-60">
            {/* Customer Selection */}
            <div>
              <label className="label flex items-center">
                Customer *
                <Tooltip title="Who is this job for?">
                  <p>Select an existing customer or create a new one.</p>
                  <p className="mt-2">The customer's address will automatically be used as the service location.</p>
                </Tooltip>
              </label>
              
              {!showInlineCustomerForm ? (
                <>
                  <select
                    required={!showInlineCustomerForm}
                    value={formData.customerId}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                    className="input"
                  >
                    <option value="">Select a customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} {customer.email ? '' : '(no email)'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowInlineCustomerForm(true)}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Create new customer
                  </button>
                </>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">New Customer</h4>
                    <button
                      type="button"
                      onClick={() => setShowInlineCustomerForm(false)}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                  
                  <div>
                    <label className="label text-sm">Name *</label>
                    <input
                      type="text"
                      value={newCustomerData.name}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                      className="input text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div>
                    <label className="label text-sm">Address *</label>
                    <input
                      type="text"
                      value={newCustomerData.address}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                      className="input text-sm"
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="label text-sm">City</label>
                      <input
                        type="text"
                        value={newCustomerData.city}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, city: e.target.value })}
                        className="input text-sm"
                        placeholder="Mooresville"
                      />
                    </div>
                    <div>
                      <label className="label text-sm">State</label>
                      <select
                        value={newCustomerData.state}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, state: e.target.value })}
                        className="input text-sm"
                      >
                        <option value="">--</option>
                        {['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
                          'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
                          'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
                          'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
                          'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'].map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label text-sm">Zip</label>
                      <input
                        type="text"
                        value={newCustomerData.zipCode}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, zipCode: e.target.value })}
                        className="input text-sm"
                        placeholder="28117"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label text-sm">Email</label>
                      <input
                        type="email"
                        value={newCustomerData.email}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                        className="input text-sm"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="label text-sm">Phone</label>
                      <input
                        type="tel"
                        value={newCustomerData.phone}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                        className="input text-sm"
                        placeholder="(980) 231-1515"
                      />
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleCreateInlineCustomer}
                    disabled={creatingCustomer}
                    className="btn btn-primary text-sm w-full"
                  >
                    {creatingCustomer ? 'Creating...' : 'Save Customer'}
                  </button>
                </div>
              )}
            </div>

            {/* ============================================ */}
            {/* ASSET SELECTION - Industry dependent */}
            {/* ============================================ */}
            {selectedCustomer && (
              <AssetSelectorSection
                customerId={parseInt(formData.customerId)}
                customerName={selectedCustomer?.name || ''}
                selectedAssetId={selectedAssetId}
                onAssetChange={(assetId, asset, extraData) => {
                  setSelectedAssetId(assetId);
                  if (extraData) {
                    setAssetExtraData(prev => ({ ...prev, ...extraData }));
                  }
                }}
                onUsesAssets={(val) => setIndustryUsesAssets(val)}
                onAddressChange={(address) => {
                  // null = revert to customer's primary address
                  if (!address) {
                    setUseDifferentAddress(false);
                    setFormData(prev => ({
                      ...prev,
                      address: customerAddress.address,
                      city: customerAddress.city,
                      state: customerAddress.state,
                      zipCode: customerAddress.zipCode,
                    }));
                  } else {
                    setUseDifferentAddress(true);
                    setFormData(prev => ({
                      ...prev,
                      address: address.address ?? address,
                      city: address.city ?? '',
                      state: address.state ?? '',
                      zipCode: address.zipCode ?? '',
                    }));
                  }
                }}
                customerAddress={customerAddress}
                extraJobData={assetExtraData}
                onExtraDataChange={setAssetExtraData}
                autoSelectPrimary={!editingJob}
                assetPatch={assetPatch}
                onSelectedAssetResolved={(a) => setSelectedProperty(a && a.assetType === 'Property' ? a : null)}
                disabled={false}
              />
            )}

            {/* Service Location fallback - only shown when industry has no assets */}
            {selectedCustomer && !industryUsesAssets && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <label className="label mb-3 flex items-center">
                  <MapPin size={16} className="mr-1.5 text-gray-500" />
                  Service Location
                </label>
                <div className="p-3 bg-white rounded-lg border border-gray-200 mb-3">
                  <p className="text-sm text-gray-900">{customerAddress.address || 'No address on file'}</p>
                  {(customerAddress.city || customerAddress.state || customerAddress.zipCode) && (
                    <p className="text-sm text-gray-600">
                      {[customerAddress.city, customerAddress.state, customerAddress.zipCode].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useDifferentAddress}
                    onChange={(e) => handleDifferentAddressToggle(e.target.checked)}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Use a different service address</span>
                </label>
                {useDifferentAddress && (
                  <div className="space-y-3 mt-3">
                    <div>
                      <label className="label text-sm">Address *</label>
                      <input
                        type="text"
                        required
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="input"
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="label text-sm">City</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="input"
                          placeholder="Mooresville"
                        />
                      </div>
                      <div>
                        <label className="label text-sm">State</label>
                        <input
                          type="text"
                          value={formData.state}
                          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          className="input"
                          placeholder="NC"
                        />
                      </div>
                      <div>
                        <label className="label text-sm">Zip</label>
                        <input
                          type="text"
                          value={formData.zipCode}
                          onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                          className="input"
                          placeholder="28117"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============================================ */}
            {/* SERVICES SECTION */}
            {/* ============================================ */}
            <div 
              className="rounded-lg overflow-hidden"
              style={{ border: `2px solid ${themeColors.border}` }}
            >
              <div 
                className="px-4 py-3 flex items-center justify-between"
                style={{ backgroundColor: themeColors.light }}
              >
                <label className="label mb-0 flex items-center">
                  <Wrench size={16} className="mr-1.5" style={{ color: themeColors.text }} />
                  Services
                  <Tooltip title="What work will you do?">
                    <p>Select the services you'll perform for this job.</p>
                    <p className="mt-2"><strong>Multiple services?</strong> Click "+ Add Service" to include additional work.</p>
                  </Tooltip>
                </label>
                {jobServices.length > 0 && jobServices[0].serviceTypeId && (
                  <button
                    type="button"
                    onClick={addService}
                    className="text-sm font-medium flex items-center gap-1 hover:opacity-80"
                    style={{ color: themeColors.text }}
                  >
                    <Plus size={16} />
                    Add Service
                  </button>
                )}
              </div>
              
              <div 
                className="p-4 space-y-4"
                style={{ backgroundColor: themeColors.lighter }}
              >
              {serviceTypes.length === 0 ? (
                <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
                  <Wrench className="mx-auto h-8 w-8 text-amber-400 mb-2" />
                  <p className="text-sm text-amber-900 font-medium">No services configured</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Go to Settings → Service Types to add your services
                  </p>
                </div>
              ) : jobServices.length === 0 ? (
                <div 
                  className="text-center py-6 text-gray-500 bg-white rounded-lg"
                  style={{ border: `2px dashed ${themeColors.border}` }}
                >
                  <Wrench className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-sm">No services added yet</p>
                  <button
                    type="button"
                    onClick={addService}
                    className="mt-2 text-sm font-medium hover:opacity-80"
                    style={{ color: themeColors.text }}
                  >
                    Add your first service
                  </button>
                </div>
              ) : (
                <>
                  {/* Invoice-style table for multiple services */}
                  {jobServices.length > 1 ? (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <div className="col-span-6">Service</div>
                        <div className="col-span-2 text-center">Qty</div>
                        <div className="col-span-3 text-right">Price</div>
                        <div className="col-span-1"></div>
                      </div>
                      
                      {jobServices.map((service, index) => {
                        const serviceType = getServiceType(service.serviceTypeId);
                        const hasCalculator = serviceType?.pricingTemplate;
                        const isEditing = expandedNotes[`editing_${index}`];
                        
                        return (
                          <div key={service.tempId || service.id}>
                            {!isEditing ? (
                              <div 
                                className={`grid grid-cols-12 gap-2 px-3 py-3 items-center cursor-pointer transition-colors ${
                                  index > 0 ? 'border-t border-gray-100' : ''
                                } ${!service.serviceTypeId ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                                onClick={() => setExpandedNotes(prev => ({ ...prev, [`editing_${index}`]: true }))}
                              >
                                <div className="col-span-6">
                                  {service.serviceTypeId ? (
                                    <p className="font-medium text-gray-900 text-sm truncate">
                                      {serviceType?.name}
                                    </p>
                                  ) : (
                                    <p className="font-medium text-amber-700 text-sm flex items-center gap-1">
                                      <span className="animate-pulse">👆</span> Click to select service...
                                    </p>
                                  )}
                                  {service.notes && (
                                    <p className="text-xs text-gray-500 truncate">{service.notes}</p>
                                  )}
                                  {hasCalculator && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded mt-1">
                                      <Calculator size={10} />
                                      Calculator
                                    </span>
                                  )}
                                </div>
                                <div className="col-span-2 text-center text-sm text-gray-600">
                                  {service.quantity || 1}
                                </div>
                                <div className="col-span-3 text-right font-semibold" style={{ color: themeColors.text }}>
                                  {service.totalPrice > 0 ? formatCurrency(service.totalPrice) : '-'}
                                </div>
                                <div className="col-span-1 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeService(index);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                    title="Remove"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div 
                                className={`px-3 py-3 ${index > 0 ? 'border-t border-gray-200' : ''}`}
                                style={{ backgroundColor: themeColors.lighter }}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium" style={{ color: themeColors.text }}>Editing Service</span>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedNotes(prev => ({ ...prev, [`editing_${index}`]: false }))}
                                    className="px-3 py-1 text-xs font-semibold text-white rounded-full shadow-sm hover:opacity-90 transition-opacity flex items-center gap-1"
                                    style={{ backgroundColor: themeColors.text }}
                                  >
                                    <Check size={12} />
                                    Done
                                  </button>
                                </div>
                                
                                <select
                                  value={service.serviceTypeId}
                                  onChange={(e) => handleServiceTypeChange(index, e.target.value)}
                                  className="input text-sm mb-2"
                                >
                                  <option value="">Select a service...</option>
                                  {serviceTypes.map((st) => {
                                    const price = st.baseRate || st.defaultPrice || 0;
                                    const template = st.pricingTemplate?.toLowerCase() || '';
                                    let unitSuffix = '';
                                    if (st.pricingTemplate && price > 0) {
                                      if (template.includes('sqft') || template.includes('sq')) {
                                        unitSuffix = '/sq ft';
                                      } else if (template.includes('linear')) {
                                        unitSuffix = '/ln ft';
                                      } else if (template.includes('hour')) {
                                        unitSuffix = '/hr';
                                      } else if (template.includes('unit') || template.includes('item')) {
                                        unitSuffix = '/ea';
                                      }
                                    }
                                    return (
                                      <option key={st.id} value={st.id}>
                                        {st.name}{st.pricingTemplate ? ' ✦' : ''}
                                      </option>
                                    );
                                  })}
                                </select>

                                {/* Pricing History Hint - Compact for multi-service */}
                                <PricingHistoryHint serviceTypeId={service.serviceTypeId} index={index} compact />

                                {/* Calculator */}
                                {serviceType?.pricingTemplate && (
                                  <div className="mb-2">
                                    <QuoteCalculator
                                      key={`${service.tempId || service.id}-${service.serviceTypeId}`}
                                      serviceType={serviceType}
                                      onQuoteCalculated={(result) => handleQuoteCalculated(index, result)}
                                      initialValues={service.initialValues || {}}
                                      property={selectedProperty}
                                      onSaveToProperty={handleSaveToProperty}
                                      customerAddress={`${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`.trim()}
                                      customerName={selectedCustomer?.name || ''}
                                    />
                                  </div>
                                )}

                                {/* Manual pricing */}
                                {service.serviceTypeId && !serviceType?.pricingTemplate && (
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs text-gray-500">Qty:</label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={service.quantity}
                                        onClick={(e) => e.target.select()}
                                        onChange={(e) => {
                                          const qty = parseFloat(e.target.value) || 1;
                                          const total = qty * (service.unitPrice || 0);
                                          updateService(index, { quantity: qty, totalPrice: total });
                                        }}
                                        className="input w-16 text-sm py-1"
                                      />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs text-gray-500">Price:</label>
                                      <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={service.totalPrice || ''}
                                          onChange={(e) => {
                                            const total = parseFloat(e.target.value) || 0;
                                            updateService(index, { 
                                              totalPrice: total,
                                              unitPrice: total / (service.quantity || 1)
                                            });
                                          }}
                                          className="input w-24 text-sm py-1 pl-6"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Notes */}
                                {service.serviceTypeId && (
                                  <input
                                    type="text"
                                    value={service.notes}
                                    onChange={(e) => updateService(index, { notes: e.target.value })}
                                    className="input text-sm py-1"
                                    placeholder="Notes (optional)"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Single Service - Simple Form */
                    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
                      {jobServices.map((service, index) => {
                        const serviceType = getServiceType(service.serviceTypeId);
                        const hasCalculator = serviceType?.pricingTemplate;
                        const hasNotes = expandedNotes[index] || service.notes;
                        
                        return (
                          <div key={service.tempId || service.id}>
                            {/* Service Type Select */}
                            <div>
                              <label className="label text-sm">Service Type</label>
                              <select
                                value={service.serviceTypeId}
                                onChange={(e) => handleServiceTypeChange(index, e.target.value)}
                                className="input"
                              >
                                <option value="">Select a service...</option>
                                {serviceTypes.map((st) => {
                                  const price = st.baseRate || st.defaultPrice || 0;
                                  const template = st.pricingTemplate?.toLowerCase() || '';
                                  let unitSuffix = '';
                                  if (price > 0) {
                                    if (template.includes('sqft') || template.includes('sq')) {
                                      unitSuffix = '/sq ft';
                                    } else if (template.includes('linear')) {
                                      unitSuffix = '/ln ft';
                                    } else if (template.includes('hour')) {
                                      unitSuffix = '/hr';
                                    } else if (template.includes('unit') || template.includes('item')) {
                                      unitSuffix = '/ea';
                                    } else if (template === 'flat' || template === 'flat-rate' || !template) {
                                      unitSuffix = '/flat rate';
                                    }
                                  }
                                  return (
                                      <option key={st.id} value={st.id}>
                                        {st.name}{st.pricingTemplate ? ' ✦' : ''}
                                      </option>
                                    );
                                })}
                              </select>

                              {/* Pricing History Hint */}
                              <PricingHistoryHint serviceTypeId={service.serviceTypeId} index={index} />
                            </div>

                            {/* Quote Calculator */}
                            {serviceType?.pricingTemplate && (
                              <div className="mt-3">
                                <QuoteCalculator
                                  key={`${service.tempId || service.id}-${service.serviceTypeId}`}
                                  serviceType={serviceType}
                                  onQuoteCalculated={(result) => handleQuoteCalculated(index, result)}
                                  initialValues={service.initialValues || {}}
                                  property={selectedProperty}
                                  onSaveToProperty={handleSaveToProperty}
                                  customerAddress={`${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`.trim()}
                                  customerName={selectedCustomer?.name || ''}
                                />
                              </div>
                            )}

                            {/* Manual price input */}
                            {service.serviceTypeId && !serviceType?.pricingTemplate && (
                              <div className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                  <label className="label text-sm">Quantity</label>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={service.quantity}
                                    onClick={(e) => e.target.select()}
                                    onChange={(e) => {
                                      const qty = parseFloat(e.target.value) || 1;
                                      const total = qty * (service.unitPrice || 0);
                                      updateService(index, { quantity: qty, totalPrice: total });
                                    }}
                                    className="input"
                                  />
                                </div>
                                <div>
                                  <label className="label text-sm">Price</label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={service.totalPrice || ''}
                                      onChange={(e) => {
                                        const total = parseFloat(e.target.value) || 0;
                                        updateService(index, { 
                                          totalPrice: total,
                                          unitPrice: total / (service.quantity || 1)
                                        });
                                      }}
                                      className="input pl-7"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Notes - Collapsible */}
                            {service.serviceTypeId && (
                              <div className="mt-2">
                                {!hasNotes ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleServiceNotes(index)}
                                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                                  >
                                    <Plus size={12} />
                                    Add note
                                  </button>
                                ) : (
                                  <div>
                                    <label className="label text-xs text-gray-500 flex items-center justify-between">
                                      <span>Note</span>
                                      {!service.notes && (
                                        <button
                                          type="button"
                                          onClick={() => toggleServiceNotes(index)}
                                          className="text-gray-400 hover:text-gray-600"
                                        >
                                          <X size={12} />
                                        </button>
                                      )}
                                    </label>
                                    <input
                                      type="text"
                                      value={service.notes}
                                      onChange={(e) => updateService(index, { notes: e.target.value })}
                                      className="input text-sm"
                                      placeholder="e.g., Front driveway only"
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Grand Total */}
              {totalQuoteAmount > 0 && (
                <div
                  className="pt-3 mt-2"
                  style={{ borderTop: `2px solid ${themeColors.border}` }}
                >
                  {isOwner && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Adjustments</p>
                  )}
                  {/* Discount Section */}
                  {isOwner && (
                    <div className="mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={discountEnabled}
                          onChange={(e) => {
                            setDiscountEnabled(e.target.checked);
                            if (!e.target.checked) setRoundedTotal(null);
                          }}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Apply Courtesy Discount</span>
                      </label>
                      {discountEnabled && (
                        <div className="mt-2 pl-6 space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {['Veteran', 'Senior', 'First Responder', 'Custom'].map(reason => (
                              <button
                                key={reason}
                                type="button"
                                onClick={() => setDiscountReason(reason)}
                                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                  discountReason === reason
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                                }`}
                              >
                                {reason === 'Veteran' ? '🎖️ Veteran' :
                                 reason === 'Senior' ? '⭐ Senior' :
                                 reason === 'First Responder' ? '🚒 First Responder' :
                                 '✏️ Custom'}
                              </button>
                            ))}
                          </div>
                          {discountReason === 'Custom' && (
                            <input
                              type="text"
                              value={discountCustomReason}
                              onChange={(e) => setDiscountCustomReason(e.target.value)}
                              className="input text-sm"
                              placeholder="e.g., Referral, Repeat Customer..."
                            />
                          )}
                          <p className="text-xs text-gray-500">
                            Shown on quote email as a courtesy acknowledgment — no price change
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Zone indicator */}
                  {isOwner && zoneInfo && (
                    <div className={`flex items-center justify-between mb-2 px-3 py-2 rounded-lg text-sm ${
                      zoneOverridden
                        ? 'bg-gray-100 text-gray-400'
                        : zoneInfo.zoneNum === 1 && zoneInfo.pct === 0
                          ? 'bg-gray-50 text-gray-400'
                          : 'bg-amber-50 border border-amber-200 text-amber-800'
                    }`}>
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="flex-shrink-0" />
                        <span className="font-medium">{zoneInfo.zoneName}</span>
                        <span className="text-xs opacity-75">· {zoneInfo.distanceMiles} mi</span>
                        {zoneInfo.pct !== 0 && (
                          <span className="text-xs font-semibold">
                            {zoneOverridden ? <s>+{zoneInfo.pct}%</s> : `+${zoneInfo.pct}%`}
                          </span>
                        )}
                        {zoneInfo.pct === 0 && (
                          <span className="text-xs opacity-60">· no adjustment</span>
                        )}
                        {zoneOverridden && (
                          <span className="text-xs italic opacity-60">ignored</span>
                        )}
                      </div>
                      {zoneInfo.pct !== 0 && (
                        <button
                          type="button"
                          onClick={() => setZoneOverridden(prev => !prev)}
                          className="text-xs underline opacity-70 hover:opacity-100 ml-2 whitespace-nowrap"
                        >
                          {zoneOverridden ? 'Restore' : 'Override'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Rounding buttons */}
                  {isOwner && displayTotal > 0 && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Round to:</span>
                      {getRoundSuggestions(displayTotal).map(val => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setRoundedTotal(roundedTotal === val ? null : val)}
                          className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                            roundedTotal === val
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-primary-400'
                          }`}
                        >
                          {formatCurrency(val)}
                        </button>
                      ))}
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                        <input
                          type="number"
                          placeholder="Custom"
                          value={roundedTotal !== null && !getRoundSuggestions(displayTotal).includes(roundedTotal) ? roundedTotal : ''}
                          onClick={(e) => e.target.select()}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setRoundedTotal(isNaN(val) ? null : val);
                          }}
                          className="input py-1 pl-5 pr-2 text-xs w-24"
                        />
                      </div>
                      {roundedTotal !== null && (
                        <button
                          type="button"
                          onClick={() => setRoundedTotal(null)}
                          className="text-xs text-gray-400 hover:text-gray-600 underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}

                  {/* Quote Total: the final line, below the adjustments */}
                  <div className="flex justify-between items-center mt-3 pt-3" style={{ borderTop: `1px solid ${themeColors.border}` }}>
                    <span className="font-semibold text-gray-900">Quote Total</span>
                    <div className="text-right">
                      {!zoneOverridden && zoneInfo && zoneInfo.pct !== 0 && (
                        <div className="text-sm text-gray-400 line-through leading-none mb-0.5">
                          {formatCurrency(serviceTotal)}
                        </div>
                      )}
                      <span className="text-2xl font-bold" style={{ color: themeColors.text }}>
                        {formatCurrency(finalTotal)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Scheduled Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label flex items-center">
                  Scheduled Date
                  {scheduledDateRequired && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                  <Tooltip>
                    <p>When will you do this job?</p>
                    <p className="mt-2">Required when status is "Scheduled" or "In Progress".</p>
                  </Tooltip>
                </label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                  className={`input ${
                    scheduledDateRequired && !formData.scheduledDate 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : ''
                  }`}
                />
                {scheduledDateRequired && !formData.scheduledDate && (
                  <p className="text-xs text-red-500 mt-1">
                    Required when status is {formData.status}
                  </p>
                )}
              </div>

              <div>
                <label className="label flex items-center gap-1.5">
                  <Clock size={14} className="text-gray-400" />
                  Scheduled Time
                  <span className="text-xs text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="space-y-2">
                  <div className="grid grid-cols-5 gap-1">
                    {[
                      { label: '7', value: '07:00' },
                      { label: '8', value: '08:00' },
                      { label: '9', value: '09:00' },
                      { label: '10', value: '10:00' },
                      { label: '11', value: '11:00' },
                      { label: '12', value: '12:00' },
                      { label: '1', value: '13:00' },
                      { label: '2', value: '14:00' },
                      { label: '3', value: '15:00' },
                      { label: '4', value: '16:00' },
                    ].map((slot) => {
                      const isAM = parseInt(slot.value.split(':')[0]) < 12;
                      const isSelected = formData.scheduledTime === slot.value;
                      return (
                        <button
                          key={slot.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, scheduledTime: slot.value })}
                          className={`py-2 text-sm rounded-lg border transition-colors ${
                            isSelected
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-700'
                          }`}
                        >
                          {slot.label}<span className="text-xs ml-0.5">{isAM ? 'a' : 'p'}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {formData.scheduledTime && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, scheduledTime: '' })}
                        className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      >
                        Clear time
                      </button>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>or pick exact:</span>
                      <input
                        type="time"
                        value={formData.scheduledTime}
                        onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                        className="input py-1 px-2 text-sm w-auto"
                      />
                    </div>
                  </div>
                </div>
                {scheduledDateRequired && !formData.scheduledTime && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Info size={12} />
                    No time = "Time TBD" in emails
                  </p>
                )}
              </div>
            </div>

            {/* Est. Duration */}
            <div>
              <label className="label flex items-center">
                Est. Duration
                <span className="ml-1 text-xs text-gray-500 font-normal">(optional)</span>
                <Tooltip>
                  <p>How long will this job take?</p>
                  <p className="mt-2">Used for route planning and scheduling.</p>
                </Tooltip>
              </label>
              <div className="relative w-1/2">
                <input
                  type="number"
                  value={formData.estimatedDurationMinutes || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    estimatedDurationMinutes: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  className="input pr-16"
                  placeholder="60"
                  min="15"
                  max="480"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                  min
                </span>
              </div>
            </div>

            <div className={isSimpleMode && !editingJob ? 'hidden' : undefined}>
              <label className="label flex items-center">
                Status
                <Tooltip title="Job Status">
                  <p><strong>Quoted:</strong> Estimate sent, waiting for approval</p>
                  <p className="mt-1"><strong>Scheduled:</strong> Confirmed and on the calendar</p>
                  <p className="mt-1"><strong>In Progress:</strong> Work has started</p>
                  <p className="mt-1"><strong>Cancelled:</strong> Job won't happen</p>
                </Tooltip>
              </label>
              {editingJob?.status === 'Completed' ? (
                <>
                  <div className="input bg-gray-100 text-gray-700 cursor-not-allowed flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Completed
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Completed jobs cannot change status.
                  </p>
                </>
              ) : (
                <>
                  <select
                    value={formData.status}
                    onChange={(e) => handleFormStatusChange(e.target.value)}
                    className="input"
                  >
                    {(!selectedCustomer?.billingType || selectedCustomer.billingType === 'PerJob') && (
                      <option value="Quoted">Quoted</option>
                    )}
                    <option value="Scheduled">Scheduled {statusHasEmailEnabled('Scheduled') ? '✉️' : ''}</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                  {(formData.status === 'Scheduled' || formData.status === 'In Progress') && (
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Use the <span className="font-medium text-green-600">Complete</span> button on the job card to mark this job done
                    </p>
                  )}
                  {selectedCustomer?.billingType && selectedCustomer.billingType !== 'PerJob' && (
                    <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-xs text-purple-800">
                        <span className="font-medium">📋 {selectedCustomer.billingType} Billing:</span> This customer is invoiced {selectedCustomer.billingType.toLowerCase()} on day {selectedCustomer.billingDay || 1}. 
                        Completed jobs will be included in their next consolidated invoice
                        {selectedCustomer.contractRate ? ` ($${selectedCustomer.contractRate}/${selectedCustomer.billingType === 'Weekly' ? 'week' : selectedCustomer.billingType === 'Monthly' ? 'month' : 'quarter'} fixed rate)` : ''}.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Confirmation Email */}
            {formData.status === 'Scheduled' && customerHasEmail && (
              <div className={`border rounded-lg p-3 ${
                confirmationAlreadySent 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                {confirmationAlreadySent ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check size={16} className="text-green-600" />
                      <span className="text-sm font-medium">
                        Confirmation email sent
                      </span>
                      {confirmationSentDate && (
                        <span className="text-xs text-green-600">
                          on {formatSentDate(confirmationSentDate)}
                        </span>
                      )}
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailOverrides.sendConfirmationEmail}
                        onChange={(e) => setEmailOverrides({ ...emailOverrides, sendConfirmationEmail: e.target.checked })}
                        className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm text-gray-700 flex items-center gap-1.5">
                          <RefreshCw size={14} className="text-blue-600" />
                          Resend confirmation email
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Send another confirmation to the customer
                        </p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailOverrides.sendConfirmationEmail}
                      onChange={(e) => setEmailOverrides({ ...emailOverrides, sendConfirmationEmail: e.target.checked })}
                      className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        <Mail size={14} className="text-blue-600" />
                        Send confirmation email to customer
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {emailSettings.sendJobConfirmation ? 'Enabled by default' : 'Disabled by default'}
                        {isOwner && (
                          <> • <a href="/settings" className="text-primary-600 hover:underline">Change default</a></>
                        )}
                      </p>
                    </div>
                  </label>
                )}
              </div>
            )}

            {formData.status === 'Scheduled' && selectedCustomer && !customerHasEmail && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">No customer email</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      This customer doesn't have an email address on file.
                    </p>
                  </div>
                </div>
              </div>
            )}

            </fieldset>

            <div>
              <label className="label">Job Notes <span className="text-xs text-gray-500 font-normal">(optional)</span></label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
                rows="2"
                placeholder="General notes, special instructions, gate codes..."
              />
            </div>
          </>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
  <button
    type="button"
    onClick={handleClose}
    disabled={isSubmitting}
    className="btn btn-secondary flex-1 justify-center"
  >
    Cancel
  </button>
  <button 
    type="submit" 
    disabled={isSubmitting}
    className="btn btn-primary flex-1 justify-center"
  >
    {isSubmitting ? (
      <span className="flex items-center gap-2">
        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
        </svg>
        {editingJob ? 'Saving...' : 'Creating...'}
      </span>
    ) : (
      editingJob 
        ? (isJobLocked ? 'Save Notes' : (isOwner ? 'Update Job' : 'Save Changes'))
        : 'Create Job'
    )}
  </button>
</div>
      </form>
    </Modal>
  );
};

export default JobFormModal;